
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UseFormReturn } from "react-hook-form";
import { IDDocumentType } from "@/hooks/admin/identity-docs/useIDTypes";
import { DatePickerField } from "@/components/profile/identity-verification/DatePickerField";
import CountrySelector from "./document-upload/CountrySelector";
import DocumentTypeSelector from "./document-upload/DocumentTypeSelector";
import DocumentNumberField from "./document-upload/DocumentNumberField";
import DocumentFileUpload from "./document-upload/DocumentFileUpload";

interface DocumentUploadSectionProps {
  title: string;
  description?: string;
  form: UseFormReturn<any>;
  countries: any[];
  idTypes: IDDocumentType[];
  onCountryChange: (value: string) => void;
  onDocumentTypeChange: (value: string) => void;
  preset?: {
    countryCode?: string;
    documentTypeId?: string;
    locked?: boolean;
  };
  sectionIndex: number;
  documentTypeFilter?: (type: IDDocumentType) => boolean;
  excludeCountries?: string[];
}

export function DocumentUploadSection({
  title,
  description,
  form,
  countries,
  idTypes,
  onCountryChange,
  onDocumentTypeChange,
  preset,
  sectionIndex,
  documentTypeFilter,
  excludeCountries = [],
}: DocumentUploadSectionProps) {
  // Determine which country is selected for this section
  const selectedCountry = form.watch(`sections.${sectionIndex}.country_code`);

  // Compute the list of document types available for this section:
  // 1. Must pass any provided filter (e.g. proves_citizenship or proves_residency)
  // 2. Must match the selected country
  // 3. Must not be in the excludeCountries list
  const availableIdTypes = idTypes
    .filter((t) => (documentTypeFilter ? documentTypeFilter(t) : true))
    .filter((t) => {
      // Include documents that match the country code OR are global documents
      return (t.country_code === selectedCountry || 
              t.global_document === true || 
              t.country_code === "GLOBAL");
    })
    .filter((t) => !excludeCountries.includes(t.country_code));

  // Determine country field label based on document type
  const countryFieldLabel = title.toLowerCase().includes("citizenship")
    ? "Country of Citizenship"
    : title.toLowerCase().includes("residency")
    ? "Country of Residency"
    : "Document Country";

  // Check if the country selector should be disabled
  const isCountrySelectorDisabled = preset?.locked === true;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{title}</CardTitle>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Country Selector */}
            <CountrySelector
              form={form}
              countries={countries}
              onCountryChange={onCountryChange}
              sectionIndex={sectionIndex}
              isDisabled={isCountrySelectorDisabled}
              excludeCountries={excludeCountries}
              label={countryFieldLabel}
              defaultCountry={preset?.countryCode}
            />

            {/* ID Document Type Selector */}
            <DocumentTypeSelector
              form={form}
              filteredIdTypes={availableIdTypes}
              onDocumentTypeChange={onDocumentTypeChange}
              sectionIndex={sectionIndex}
            />
          </div>

          {/* Document Number */}
          <DocumentNumberField form={form} sectionIndex={sectionIndex} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Issue Date using custom DatePickerField - removed (Optional) text */}
            <DatePickerField
              form={form}
              name={`sections.${sectionIndex}.issue_date`}
              label="Issue Date"
              description="When the document was issued"
            />

            {/* Expiry Date using custom DatePickerField - removed (Optional) text */}
            <DatePickerField
              form={form}
              name={`sections.${sectionIndex}.expiry_date`}
              label="Expiry Date"
              description="When the document expires (leave blank if no expiry)"
            />
          </div>

          {/* File Upload */}
          <DocumentFileUpload form={form} sectionIndex={sectionIndex} />
        </div>
      </CardContent>
    </Card>
  );
}
